import {
    InterestIncomeWorkingPaper, SalesIncomeWorkingPaper
} from "@/services/auditService/interface/income";
import { PayloadStrategy } from "@/pages/components/audit/adjustment/adapters/types";

// Sales Income strategy (QI1): ensure payload conforms to DonationWorkingPaper.
// Amount is handled via adjustmentAmount in the entry; payload holds structured working paper details.
export const salesIncomeStrategy: PayloadStrategy = {
    buildPayload: ({ prev, ctx }) => {
        const p = (prev || {}) as Partial<SalesIncomeWorkingPaper>;
        const annualId = ctx?.annualId;
        
        // Helper function to add annualId to each row
        const addAnnualId = <T extends Record<string, any>>(rows: T[]): T[] => {
            if (!Array.isArray(rows)) return rows;
            return rows.map(row => ({
                ...row,
                annualId: annualId
            }));
        };
        
        const salesIncome = addAnnualId(Array.isArray(p.salesIncome) ? p.salesIncome : []);
        const relatedRows = addAnnualId(Array.isArray(p.relatedRows) ? p.relatedRows : []);
        const transactionTest = addAnnualId(Array.isArray(p.transactionTest) ? p.transactionTest : []);
        const cutOffThisYearTest = addAnnualId(Array.isArray(p.cutOffThisYearTest) ? p.cutOffThisYearTest : []);
        const cutOffNextYearTest = addAnnualId(Array.isArray(p.cutOffNextYearTest) ? p.cutOffNextYearTest : []);
        
        const next: any = {};
        if (salesIncome.length > 0) next.salesIncome = salesIncome;
        // 当 relatedRows 存在时（即使为空），也包含它，因为前端传递了这个字段就表示用户操作过
        // 如果前端不传递（undefined），则不包含
        if (p.relatedRows !== undefined) {
            next.relatedRows = relatedRows;
        }
        if (transactionTest.length > 0) next.transactionTest = transactionTest;
        // 始终包含 cutOff 数据，即使为空数组，以便正确保存用户的输入状态
        next.cutOffThisYearTest = cutOffThisYearTest;
        next.cutOffNextYearTest = cutOffNextYearTest;
        
        // Add annualInfo if present
        if (p.annualInfo) {
            next.annualInfo = {
                ...p.annualInfo,
                annualId: annualId
            };
        }
        
        return next;
    },
    // Return undefined to let registry fall back to entry.adjustmentAmount
    readAmount: (payload: any) => {
        const n = Number((payload || {}).adjustment);
        return Number.isFinite(n) ? n : (undefined as any);
    },
};

// Interest Income Strategy(QXI1): ensure payload conforms to DonationWorkingPaper.
// Amount is handled via adjustmentAmount in the entry; payload holds structured working paper details.
export const interestIncomeStrategy: PayloadStrategy = {
    buildPayload: ({ prev, ctx }) => {
        const p = (prev || {}) as Partial<InterestIncomeWorkingPaper>;
        const annualId = ctx?.annualId;
        
        // Helper function to add annualId to each row
        const addAnnualId = <T extends Record<string, any>>(rows: T[]): T[] => {
            if (!Array.isArray(rows)) return rows;
            return rows.map(row => ({
                ...row,
                annualId: annualId
            }));
        };
        
        const bankInterestIncome = addAnnualId(Array.isArray(p.bankInterestIncome) ? p.bankInterestIncome : []);
        const otherInterestIncome = addAnnualId(Array.isArray(p.otherInterestIncome) ? p.otherInterestIncome : []);
        const relatedRows = addAnnualId(Array.isArray(p.relatedRows) ? p.relatedRows : []);
        
        const next: any = {};
        if (bankInterestIncome.length > 0) next.bankInterestIncome = bankInterestIncome;
        if (otherInterestIncome.length > 0) next.otherInterestIncome = otherInterestIncome;
        if (relatedRows.length > 0) next.relatedRows = relatedRows;
        
        return next;
    },
    // Return undefined to let registry fall back to entry.adjustmentAmount
    readAmount: (payload: any) => {
        const n = Number((payload || {}).adjustment);
        return Number.isFinite(n) ? n : (undefined as any);
    },
};